import {
  keepsTextEditing,
  nextTextEditReentryGuard,
} from "../text-editing";
import { locateBlockById } from "../find";
import {
  deriveBlockMultiSelectionFromSelection,
  type BlockMultiSelection,
} from "../selection/block-multi-selection";
import {
  deriveRowMultiSelectionFromSelection,
  type RowMultiSelection,
} from "../selection/row-multi-selection";
import type { Selection } from "../types";
import type { EditorDraft } from "./types";

export function syncBlockMultiSelectionFromSelection(draft: EditorDraft): void {
  const derived = deriveBlockMultiSelectionFromSelection(draft.selection);
  draft.selectedBlockIds = derived.selectedBlockIds;
  draft.primaryBlockId = derived.primaryBlockId;
  draft.anchorBlockId = derived.anchorBlockId;
}

export function syncRowMultiSelectionFromSelection(draft: EditorDraft): void {
  const derived = deriveRowMultiSelectionFromSelection(draft.selection);
  draft.selectedRowIds = derived.selectedRowIds;
  draft.primaryRowId = derived.primaryRowId;
  draft.anchorRowId = derived.anchorRowId;
}

export function applyBlockMultiSelectionChange(
  draft: EditorDraft,
  blockSelection: BlockMultiSelection,
): void {
  draft.selectedBlockIds = [...blockSelection.selectedBlockIds];
  draft.primaryBlockId = blockSelection.primaryBlockId;
  draft.anchorBlockId = blockSelection.anchorBlockId;
  draft.selectedRowIds = [];
  draft.primaryRowId = null;
  draft.anchorRowId = null;

  const primaryBlockId = blockSelection.primaryBlockId;
  if (primaryBlockId === null) {
    return;
  }

  const located = locateBlockById(draft.document, primaryBlockId);
  if (!located) {
    return;
  }

  draft.selection = {
    kind: "block",
    rowId: located.rowId,
    columnId: located.columnId,
    blockId: located.blockId,
  };
  if (!keepsTextEditing(draft.selection, draft.textEditingBlock)) {
    draft.textEditingBlock = null;
  }
  draft.textEditReentryGuard = nextTextEditReentryGuard(
    draft.selection,
    draft.textEditReentryGuard,
  );
}

export function applyRowMultiSelectionChange(
  draft: EditorDraft,
  rowSelection: RowMultiSelection,
): void {
  draft.selectedRowIds = [...rowSelection.selectedRowIds];
  draft.primaryRowId = rowSelection.primaryRowId;
  draft.anchorRowId = rowSelection.anchorRowId;
  draft.selectedBlockIds = [];
  draft.primaryBlockId = null;
  draft.anchorBlockId = null;

  const primaryRowId = rowSelection.primaryRowId;
  if (primaryRowId === null) {
    return;
  }

  draft.selection = { kind: "row", rowId: primaryRowId };
  if (!keepsTextEditing(draft.selection, draft.textEditingBlock)) {
    draft.textEditingBlock = null;
  }
  draft.textEditReentryGuard = nextTextEditReentryGuard(
    draft.selection,
    draft.textEditReentryGuard,
  );
}

export function applySelectionChange(
  draft: EditorDraft,
  selection: Selection,
): void {
  draft.selection = selection;
  syncBlockMultiSelectionFromSelection(draft);
  syncRowMultiSelectionFromSelection(draft);
  if (!keepsTextEditing(selection, draft.textEditingBlock)) {
    draft.textEditingBlock = null;
  }
  draft.textEditReentryGuard = nextTextEditReentryGuard(
    selection,
    draft.textEditReentryGuard,
  );
}
